import { createContext, ReactNode, useContext, useState } from 'react'

export type UserRole =
  | 'user'
  | 'program_administrator'
  | 'manager'
  | 'system_admin'
  | 'regional_manager'
export type ChannelRole =
  | 'dealer_staff'
  | 'dealer_principal'
  | 'channel_account_manager'
  | 'channel_program_manager'
export type ProgramType = 'sales' | 'channel'

export interface Region {
  id: string
  name: string
  managerId: string
}

export interface Team {
  id: string
  name: string
  regionId: string
  managerId: string
  memberCount: number
}

export interface Dealer {
  id: string
  name: string
  territory: string
  channelAccountManagerId: string
  locationCount: number
}

export interface DealerLocation {
  id: string
  dealerId: string
  name: string
  principalId: string
  staffCount: number
}

export interface User {
  id: string
  name: string
  role: UserRole | ChannelRole
  tier?: string
  initials?: string
  teamId?: string
  regionId?: string
  managedTeamId?: string
  managedRegionId?: string
  programType?: ProgramType
  dealerId?: string
  dealerLocationId?: string
  managedDealerId?: string
  managedTerritoryIds?: string[]
}

interface UserContextType {
  currentUser: User
  setCurrentUser: (user: User) => void
  isUserSelected: boolean
  setIsUserSelected: (selected: boolean) => void
  programType: ProgramType | null
  setProgramType: (type: ProgramType | null) => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const REGIONS: Region[] = [
  { id: 'region-1', name: 'Northeast', managerId: 'rm-1' },
  { id: 'region-2', name: 'Southeast', managerId: 'rm-2' },
  { id: 'region-3', name: 'Central', managerId: 'rm-3' },
  { id: 'region-4', name: 'Southwest', managerId: 'rm-4' },
  { id: 'region-5', name: 'West', managerId: 'rm-5' },
  { id: 'region-6', name: 'International', managerId: 'rm-6' },
]

function generateTeams(): Team[] {
  const teams: Team[] = []
  const teamNames = [
    'Alpha',
    'Beta',
    'Gamma',
    'Delta',
    'Epsilon',
    'Zeta',
    'Eta',
    'Theta',
  ]

  REGIONS.forEach((region, regionIndex) => {
    teamNames.forEach((teamName, teamIndex) => {
      const teamId = `team-${regionIndex * 8 + teamIndex + 1}`
      const managerId = `manager-${regionIndex * 8 + teamIndex + 1}`
      teams.push({
        id: teamId,
        name: `${region.name} ${teamName}`,
        regionId: region.id,
        managerId: managerId,
        memberCount: 20 + (teamIndex % 3),
      })
    })
  })

  return teams
}

export const TEAMS = generateTeams()

const FIRST_NAMES = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'William',
  'Barbara',
  'David',
  'Elizabeth',
  'Richard',
  'Susan',
  'Joseph',
  'Jessica',
  'Thomas',
  'Sarah',
  'Christopher',
  'Karen',
  'Charles',
  'Lisa',
  'Daniel',
  'Nancy',
  'Matthew',
  'Betty',
  'Anthony',
  'Margaret',
  'Mark',
  'Sandra',
  'Donald',
  'Ashley',
  'Steven',
  'Kimberly',
  'Paul',
  'Emily',
  'Andrew',
  'Donna',
  'Joshua',
  'Michelle',
  'Kenneth',
  'Carol',
  'Kevin',
  'Amanda',
  'Brian',
  'Dorothy',
  'George',
  'Melissa',
  'Timothy',
  'Deborah',
  'Ronald',
  'Stephanie',
  'Edward',
  'Rebecca',
  'Jason',
  'Sharon',
  'Jeffrey',
  'Laura',
  'Ryan',
  'Cynthia',
  'Jacob',
  'Kathleen',
  'Gary',
  'Amy',
  'Nicholas',
  'Angela',
  'Eric',
  'Shirley',
  'Jonathan',
  'Anna',
  'Stephen',
  'Brenda',
  'Larry',
  'Pamela',
  'Justin',
  'Emma',
  'Scott',
  'Nicole',
  'Brandon',
  'Helen',
  'Benjamin',
  'Samantha',
  'Samuel',
  'Katherine',
  'Raymond',
  'Christine',
  'Gregory',
  'Debra',
  'Frank',
  'Rachel',
  'Alexander',
  'Carolyn',
  'Patrick',
  'Janet',
  'Raymond',
  'Catherine',
  'Jack',
  'Maria',
  'Dennis',
  'Heather',
  'Jerry',
  'Diane',
  'Tyler',
  'Ruth',
  'Aaron',
  'Julie',
  'Jose',
  'Olivia',
  'Adam',
  'Joyce',
  'Nathan',
  'Virginia',
  'Zachary',
  'Victoria',
  'Douglas',
  'Kelly',
  'Peter',
  'Lauren',
  'Henry',
  'Christina',
]

const LAST_NAMES = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Perez',
  'Thompson',
  'White',
  'Harris',
  'Sanchez',
  'Clark',
  'Ramirez',
  'Lewis',
  'Robinson',
  'Walker',
  'Young',
  'Allen',
  'King',
  'Wright',
  'Scott',
  'Torres',
  'Nguyen',
  'Hill',
  'Flores',
  'Green',
  'Adams',
  'Nelson',
  'Baker',
  'Hall',
  'Rivera',
  'Campbell',
  'Mitchell',
  'Carter',
  'Roberts',
  'Gomez',
  'Phillips',
  'Evans',
  'Turner',
  'Diaz',
  'Parker',
  'Cruz',
  'Edwards',
  'Collins',
  'Reyes',
  'Stewart',
  'Morris',
  'Morales',
  'Murphy',
  'Cook',
  'Rogers',
  'Gutierrez',
  'Ortiz',
  'Morgan',
  'Cooper',
  'Peterson',
  'Bailey',
  'Reed',
  'Kelly',
  'Howard',
  'Ramos',
  'Kim',
  'Cox',
  'Ward',
  'Richardson',
  'Watson',
  'Brooks',
  'Chavez',
  'Wood',
  'James',
  'Bennett',
  'Gray',
  'Mendoza',
  'Ruiz',
  'Hughes',
  'Price',
  'Alvarez',
  'Castillo',
  'Sanders',
  'Patel',
  'Myers',
  'Long',
  'Ross',
  'Foster',
  'Jimenez',
  'Powell',
  'Jenkins',
  'Perry',
  'Russell',
]

const TIERS = ['Bronze', 'Silver', 'Gold', 'Platinum']

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
}

function hashSeed(seed: number): number {
  let h = seed ^ 0xdeadbeef
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b)
  h = Math.imul(h ^ (h >>> 13), 0x45d9f3b)
  h = (h ^ (h >>> 16)) >>> 0
  return h
}

function generateName(seed: number): string {
  const h = hashSeed(seed)
  const firstName = FIRST_NAMES[h % FIRST_NAMES.length]
  const lastName = LAST_NAMES[(h >>> 8) % LAST_NAMES.length]
  return `${firstName} ${lastName}`
}

function generateTier(seed: number): string {
  const rand = seed % 100
  if (rand < 30) return 'Bronze'
  if (rand < 65) return 'Silver'
  if (rand < 90) return 'Gold'
  return 'Platinum'
}

const ORIGINAL_TEST_USERS = {
  user: {
    id: 'sp-1',
    name: 'Olivia Murphy',
    role: 'user' as UserRole,
    tier: 'Silver',
    initials: 'OM',
    teamId: 'team-1',
    regionId: 'region-1',
    programType: 'sales' as ProgramType,
  },
  manager: {
    id: 'manager-1',
    name: 'Marcus Hayes',
    role: 'manager' as UserRole,
    initials: 'MH',
    managedTeamId: 'team-1',
    teamId: 'team-1',
    regionId: 'region-1',
    programType: 'sales' as ProgramType,
  },
}

function generateRegionalManagers(): User[] {
  const regionalManagers: User[] = []
  REGIONS.forEach((region, index) => {
    const name = generateName(5000 + index)
    regionalManagers.push({
      id: region.managerId,
      name: name,
      role: 'regional_manager',
      initials: getInitials(name),
      managedRegionId: region.id,
      regionId: region.id,
      programType: 'sales',
    })
  })
  return regionalManagers
}

function generateManagers(): User[] {
  const managers: User[] = []
  managers.push(ORIGINAL_TEST_USERS.manager)

  TEAMS.forEach((team, index) => {
    if (team.id === 'team-1') return
    const name = generateName(6000 + index)
    managers.push({
      id: team.managerId,
      name: name,
      role: 'manager',
      initials: getInitials(name),
      managedTeamId: team.id,
      teamId: team.id,
      regionId: team.regionId,
      programType: 'sales',
    })
  })
  return managers
}

function generateUsers(): User[] {
  const users: User[] = []
  users.push(ORIGINAL_TEST_USERS.user)
  let userId = 2

  TEAMS.forEach((team, teamIndex) => {
    const startIndex = team.id === 'team-1' ? 1 : 0
    const membersToGenerate =
      team.id === 'team-1' ? team.memberCount - 1 : team.memberCount

    for (let i = startIndex; i < startIndex + membersToGenerate; i++) {
      const seed = teamIndex * 100 + i
      const name = generateName(seed)
      users.push({
        id: `sp-${userId}`,
        name: name,
        role: 'user',
        tier: generateTier(seed),
        initials: getInitials(name),
        teamId: team.id,
        regionId: team.regionId,
        programType: 'sales',
      })
      userId++
    }
  })

  return users
}

const REGIONAL_MANAGERS = generateRegionalManagers()
const MANAGERS = generateManagers()
const USERS_LIST = generateUsers()

export const USERS = {
  user: ORIGINAL_TEST_USERS.user,
  program_administrator: {
    id: 'pa-1',
    name: 'Sarah Jenkins',
    role: 'program_administrator' as UserRole,
    initials: 'SJ',
    programType: 'sales' as ProgramType,
  } as User,
  manager: ORIGINAL_TEST_USERS.manager,
  regional_manager: REGIONAL_MANAGERS[0],
  system_admin: {
    id: 'sa-1',
    name: 'Riley Stevens',
    role: 'system_admin' as UserRole,
    initials: 'RS',
    programType: 'sales' as ProgramType,
  } as User,
}

export const ALL_USERS = [
  ...USERS_LIST,
  ...MANAGERS,
  ...REGIONAL_MANAGERS,
  USERS.program_administrator,
  USERS.system_admin,
]

// ============================================================
// DEALER INCENTIVE PROGRAM DATA
// ============================================================

const CHANNEL_TERRITORIES = [
  { id: 'territory-1', name: 'Northeast' },
  { id: 'territory-2', name: 'Southeast' },
  { id: 'territory-3', name: 'Central' },
  { id: 'territory-4', name: 'Southwest' },
  { id: 'territory-5', name: 'West' },
]

const DEALER_NAMES = [
  'Apex Auto Group',
  'Summit Parts Network',
  'Northern Drive Group',
  'Velocity Service Network',
  'Pacific Brake Partners',
  'Heartland Auto Supply',
  'Atlantic Service Centers',
  'Pinnacle Parts Plus',
  'Frontier Brake Specialists',
  'Coastal Auto Partners',
  'Mountain View Service',
  'Great Lakes Parts Supply',
]

export const DEALERS: Dealer[] = DEALER_NAMES.map((name, i) => ({
  id: `dealer-${i + 1}`,
  name: name,
  territory: CHANNEL_TERRITORIES?.[i % CHANNEL_TERRITORIES.length]?.id,
  channelAccountManagerId: `cam-${Math.floor(i / 3) + 1}`,
  locationCount: i < 6 ? 3 : 2,
})) as any

function generateDealerLocations(): DealerLocation[] {
  const locations: DealerLocation[] = []
  let locId = 1
  const storeNames: Record<string, string[]> = {
    'dealer-1': [
      'Calgary North Store',
      'Edmonton West Location',
      'Red Deer Service Center',
    ],
    'dealer-2': [
      'Vancouver Central Store',
      'Surrey Service Center',
      'Burnaby Parts Location',
    ],
    'dealer-3': [
      'Toronto East Store',
      'Mississauga Service Center',
      'Hamilton Retail Location',
    ],
    'dealer-4': [
      'Ottawa Central Store',
      'Kingston Parts Location',
      'Barrie Service Center',
    ],
    'dealer-5': [
      'Winnipeg Main Store',
      'Regina Retail Location',
      'Saskatoon Service Center',
    ],
    'dealer-6': ['Denver Parts Store', 'Colorado Springs Location'],
    'dealer-7': ['Portland Service Center', 'Salem Retail Store'],
    'dealer-8': ['Phoenix North Store', 'Tucson Parts Location'],
    'dealer-9': ['Dallas Central Store', 'Fort Worth Retail Location'],
    'dealer-10': ['San Diego Service Center', 'Oceanside Parts Store'],
    'dealer-11': ['Chicago West Store', 'Milwaukee Retail Location'],
    'dealer-12': ['Detroit North Store', 'Grand Rapids Service Center'],
  }
  DEALERS.forEach((dealer) => {
    const count = dealer.locationCount
    const names = storeNames[dealer.id] || []
    for (let i = 0; i < count; i++) {
      locations.push({
        id: `loc-${locId}`,
        dealerId: dealer.id,
        name: names[i] || `Store ${i + 1}`,
        principalId: `dp-${locId}`,
        staffCount: 8 + (locId % 5),
      })
      locId++
    }
  })
  return locations
}

export const DEALER_LOCATIONS = generateDealerLocations()

function generateChannelAccountManagers(): User[] {
  const cams: User[] = []
  const camCount = 4
  for (let i = 0; i < camCount; i++) {
    const name = generateName(8000 + i)
    const dealersForCAM = DEALERS.filter(
      (d) => d.channelAccountManagerId === `cam-${i + 1}`,
    )
    const territoryIds = Array.from(
      new Set(dealersForCAM.map((d) => d.territory)),
    )
    cams.push({
      id: `cam-${i + 1}`,
      name: name,
      role: 'channel_account_manager' as ChannelRole,
      initials: getInitials(name),
      programType: 'channel',
      managedTerritoryIds: territoryIds,
    })
  }
  return cams
}

function generateDealerPrincipals(): User[] {
  const principals: User[] = []
  DEALER_LOCATIONS.forEach((loc) => {
    const dealer = DEALERS.find((d) => d.id === loc.dealerId)
    const locIndex = parseInt(loc?.id?.split?.('-')?.[1] ?? '')
    const name = generateName(9000 + locIndex)
    principals.push({
      id: loc.principalId,
      name: name,
      role: 'dealer_principal' as ChannelRole,
      initials: getInitials(name),
      programType: 'channel',
      dealerId: loc.dealerId,
      dealerLocationId: loc.id,
      managedDealerId: loc.dealerId,
    })
  })
  return principals
}

function generateDealerStaff(): User[] {
  const staff: User[] = []
  let staffId = 1
  DEALER_LOCATIONS.forEach((loc) => {
    for (let i = 0; i < loc.staffCount; i++) {
      const seed = 10000 + staffId
      const name = generateName(seed)
      staff.push({
        id: `ds-${staffId}`,
        name: name,
        role: 'dealer_staff' as ChannelRole,
        tier: generateTier(seed),
        initials: getInitials(name),
        programType: 'channel',
        dealerId: loc.dealerId,
        dealerLocationId: loc.id,
      })
      staffId++
    }
  })
  return staff
}

const CHANNEL_ACCOUNT_MANAGERS = generateChannelAccountManagers()
const DEALER_PRINCIPALS = generateDealerPrincipals()
const DEALER_STAFF = generateDealerStaff()

export const CHANNEL_USERS = {
  channel_program_manager: {
    id: 'cpm-1',
    name: 'Diana Chen',
    role: 'channel_program_manager' as ChannelRole,
    initials: 'DC',
    programType: 'channel' as ProgramType,
  } as User,
  channel_account_manager: CHANNEL_ACCOUNT_MANAGERS[0],
  dealer_principal: DEALER_PRINCIPALS[0],
  dealer_staff: DEALER_STAFF[0],
}

export const CHANNEL_ALL_USERS: User[] = [
  ...DEALER_STAFF,
  ...DEALER_PRINCIPALS,
  ...CHANNEL_ACCOUNT_MANAGERS,
  CHANNEL_USERS.channel_program_manager,
]

// ============================================================
// HELPER FUNCTIONS (Sales)
// ============================================================

export function getUsersByTeam(teamId: string): User[] {
  return ALL_USERS.filter((user) => user.teamId === teamId)
}

export function getUsersByRegion(regionId: string): User[] {
  return ALL_USERS.filter((user) => user.regionId === regionId)
}

export function getTeamsByRegion(regionId: string): Team[] {
  return TEAMS.filter((team) => team.regionId === regionId)
}

export function getRegionById(regionId: string): Region | undefined {
  return REGIONS.find((region) => region.id === regionId)
}

export function getTeamById(teamId: string): Team | undefined {
  return TEAMS.find((team) => team.id === teamId)
}

export function getUserById(userId: string): User | undefined {
  return (
    ALL_USERS.find((user) => user.id === userId) ||
    CHANNEL_ALL_USERS.find((user) => user.id === userId)
  )
}

export function getOrganizationStats() {
  return {
    totalUsers: USERS_LIST.length,
    totalManagers: MANAGERS.length,
    totalRegionalManagers: REGIONAL_MANAGERS.length,
    totalRegions: REGIONS.length,
    totalTeams: TEAMS.length,
    totalAllUsers: ALL_USERS.length,
  }
}

// ============================================================
// HELPER FUNCTIONS (Channel)
// ============================================================

export function getDealerById(dealerId: string): Dealer | undefined {
  return DEALERS.find((d) => d.id === dealerId)
}

export function getDealerLocationById(
  locationId: string,
): DealerLocation | undefined {
  return DEALER_LOCATIONS.find((l) => l.id === locationId)
}

export function getLocationsByDealer(dealerId: string): DealerLocation[] {
  return DEALER_LOCATIONS.filter((l) => l.dealerId === dealerId)
}

export function getDealerStaffByLocation(locationId: string): User[] {
  return DEALER_STAFF.filter((s) => s.dealerLocationId === locationId)
}

export function getDealerStaffByDealer(dealerId: string): User[] {
  return DEALER_STAFF.filter((s) => s.dealerId === dealerId)
}

export function getDealersByCAM(camId: string): Dealer[] {
  return DEALERS.filter((d) => d.channelAccountManagerId === camId)
}

export function getPrincipalForLocation(locationId: string): User | undefined {
  return DEALER_PRINCIPALS.find((p) => p.dealerLocationId === locationId)
}

export function getCAMById(camId: string): User | undefined {
  return CHANNEL_ACCOUNT_MANAGERS.find((c) => c.id === camId)
}

export function getChannelOrganizationStats() {
  return {
    totalDealers: DEALERS.length,
    totalLocations: DEALER_LOCATIONS.length,
    totalDealerStaff: DEALER_STAFF.length,
    totalPrincipals: DEALER_PRINCIPALS.length,
    totalCAMs: CHANNEL_ACCOUNT_MANAGERS.length,
    totalChannelUsers: CHANNEL_ALL_USERS.length,
  }
}

// ============================================================
// PROVIDER
// ============================================================

export function UserProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User>(USERS.user)
  const [isUserSelected, setIsUserSelected] = useState<boolean>(false)
  const [programType, setProgramType] = useState<ProgramType | null>(null)

  return (
    <UserContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        isUserSelected,
        setIsUserSelected,
        programType,
        setProgramType,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
